import React, { useState, useEffect } from "react";
import Reviews from "./ReviewTab";
import OrderHistory from "./OrderHistory";
import PersonalInfo from "./PersonalInfo";
import ProfileImage from "./ProfileImage";
import AddressTab from "./AddressTab";
import { motion } from "framer-motion";
import { 
  User, 
  ShoppingBag, 
  Home, 
  Star,
  LogOut,
} from "lucide-react";
import { useClerk, useUser } from "@clerk/clerk-react";

export default function UserProfile() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState("personal-info");
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { signOut } = useClerk();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`/api/user/${user.id}`);
        const data = await response.json();
        setAccount({
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          phone: data.phone || "",
          email: user.primaryEmailAddress?.emailAddress || "",
          img_url: data.img_url || "",
        });
        setAddresses(data.addresses || []);
        setOrders(data.orders || []);
        setReviews(data.reviews || []);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchUserProfile();
    }
  }, [user?.id]);

  const tabs = [
    {
      id: "personal-info",
      name: "Your Profile",
      icon: <User className="h-5 w-5" />, 
    },
    {
      id: "order-history",
      name: "Your Orders",
      icon: <ShoppingBag className="h-5 w-5" />, 
    },
    {
      id: "address",
      name: "Your Addresses",
      icon: <Home className="h-5 w-5" />, 
    },
    {
      id: "review",
      name: "Your Reviews",
      icon: <Star className="h-5 w-5" />, 
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-pulse space-y-4">
          <div className="h-12 w-12 bg-gray-200 rounded-full mx-auto"></div>
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Navigation Tabs */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-40 flex overflow-x-auto p-2 space-x-2 scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center justify-center p-3 text-sm rounded-lg transition-all ${
              activeTab === tab.id ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab.icon}
            <span className="mt-1">{tab.name}</span>
          </button>
        ))}
      </div>

      <div className="container mx-auto px-4 py-20 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar for larger screens */}
          <div className="hidden lg:block lg:col-span-3 bg-white rounded-xl shadow p-6">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center p-4 rounded-xl transition-all ${
                    activeTab === tab.id ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {tab.icon}
                  <span className="ml-4 font-medium">{tab.name}</span>
                </button>
              ))}
            </nav>

            <div className="mt-6">
              <button
                onClick={() => signOut()}
                className="w-full flex items-center justify-center space-x-2 p-4 bg-red-50 text-red-600 rounded-xl"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {activeTab === "personal-info" && <PersonalInfo account={account} />}
            {activeTab === "order-history" && <OrderHistory orders={orders} />}
            {activeTab === "address" && <AddressTab addresses={addresses} />}
            {activeTab === "review" && <Reviews reviews={reviews} />}
          </div>
        </div>
      </div>
    </div>
  );
}
